import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import { updateFolderAsync } from '../../store/slices/folderSlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * EDIT FOLDER MODAL
 *
 * Modal dialog for editing/renaming a folder.
 */
function EditFolderModal({
  isOpen,
  onClose,
  folder
}) {
  const dispatch = useDispatch()
  const { showError, showWarning } = useNotification()

  // Form state
  const [name, setName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize form with folder data when modal opens
  useEffect(() => {
    if (folder && isOpen) {
      setName(folder.name || '')
    }
  }, [folder, isOpen])

  // Handle form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarning('Validation Error', 'Please enter a folder name')
      return
    }

    if (!folder?.id) {
      return
    }

    setIsSubmitting(true)

    try {
      await dispatch(updateFolderAsync({
        folderId: folder.id,
        updates: { name: name.trim() }
      })).unwrap()

      onClose()
    } catch (error) {
      console.error('Failed to update folder:', error)
      const errorMessage = error?.message || 'Failed to update folder. Please try again.'
      showError('Update Folder Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setName('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Rename Folder
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  marginBottom: '8px'
                }}
              >
                Folder Name *
              </label>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Regression Tests"
                isRequired
                autoFocus
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!name.trim() || name.trim() === folder?.name}
            >
              Save
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { EditFolderModal }
